import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Loader2, XCircle } from 'lucide-react';
import { readAndClearAppleState } from '../services/appleAuth';
import { mapSocialAuthError } from '../utils/socialAuthError';

// TREATX-APPLE-REAL-UAT-MISSING-ID-TOKEN-01
//
// Apple's Web SSO uses response_mode=form_post whenever we request the
// `name email` scope. Apple POSTs directly to a backend endpoint
// `/api/auth/apple/callback`, which verifies the credential, stashes
// the full login payload behind a one-time handle in Mongo, and 302s
// the browser to this HashRouter page with `?handle=<h>&state=<s>`.
//
// This component:
//   1) Reads `handle` / `state` / `error` from the HashRouter query.
//   2) Verifies state (CSRF).
//   3) POSTs `handle` to /api/auth/apple/exchange, receives the full
//      social-login result payload.
//   4) Applies the same localStorage side-effects `applyLoginResult`
//      uses on the login page, then hard-navigates to the target
//      dashboard — the RBACProvider re-hydrates from localStorage on
//      the fresh render.
//
// If the URL still lands with a legacy raw fragment (defence in depth,
// e.g. dev hot-reload race before src/index.js normaliser runs) we
// fall back to reading raw params from `window.location.hash`.
export default function AppleCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [error, setError] = useState(null);
  // TREATX-SOCIAL-REG-CORRECTNESS-01: semantic, localized outcome (distinguishes
  // registration-not-completed gates from auth failures). Falls back to the
  // legacy code map only when a specific gate code is not present.
  const [errInfo, setErrInfo] = useState(null);

  useEffect(() => {
    (async () => {
      // Prefer HashRouter query (post-normaliser); fall back to hash.
      const routeSearch = (location.search || '').replace(/^\?/, '');
      const rawHash = (window.location.hash || '').replace(/^#/, '');
      const hashParams =
        rawHash.includes('=') && !rawHash.startsWith('/')
          ? rawHash
          : '';
      const params = new URLSearchParams(routeSearch || hashParams);

      const errCode = params.get('error');
      const state = params.get('state');
      const handle = params.get('handle');
      // Legacy fragment path (if Apple ever delivered id_token here)
      const legacyIdToken = params.get('id_token');

      // ── CSRF: compare returned state with sessionStorage
      const expected = readAndClearAppleState();
      if (state && expected && state !== expected) {
        setError('state_mismatch');
        return;
      }

      if (errCode) {
        setError(errCode);
        return;
      }

      // ── Preferred path: backend one-time handle
      if (handle) {
        try {
          const apiBase = process.env.REACT_APP_BACKEND_URL || '';
          const resp = await axios.post(`${apiBase}/api/auth/apple/exchange`, { handle });
          const data = resp?.data || {};
          applySocialLoginResult(data);
          const dest = data.redirect_path || '/patient/dashboard';
          // Hard reload so RBAC/Auth providers re-hydrate from localStorage.
          window.location.assign(`/#${dest.startsWith('/') ? dest : '/' + dest}`);
          return;
        } catch (e) {
          const status = e?.response?.status;
          const detail = e?.response?.data?.detail;
          const code = (detail && typeof detail === 'object') ? detail.code : undefined;
          if (code) setErrInfo(mapSocialAuthError(t, status, code));
          if (status === 404) setError('handle_expired');
          else if (status === 503) setError(code || 'coming_soon');
          else setError(code || 'exchange_failed');
        }
        return;
      }

      // ── Legacy fragment path (should not occur with form_post but
      //    kept as defence in depth for any residual dev flow).
      if (legacyIdToken) {
        try {
          const apiBase = process.env.REACT_APP_BACKEND_URL || '';
          const resp = await axios.post(`${apiBase}/api/auth/social/apple`, {
            id_token: legacyIdToken,
            name: (() => {
              try {
                const c = sessionStorage.getItem('_treatx_apple_pending_name');
                sessionStorage.removeItem('_treatx_apple_pending_name');
                return c || null;
              } catch { return null; }
            })(),
          });
          const data = resp?.data || {};
          if (data.success || data.user) {
            applySocialLoginResult(data);
            const dest = data.redirect_path || '/patient/dashboard';
            window.location.assign(`/#${dest.startsWith('/') ? dest : '/' + dest}`);
            return;
          }
          setError(data.message || 'login_failed');
        } catch (e) {
          const status = e?.response?.status;
          const detail = e?.response?.data?.detail;
          const code = (detail && typeof detail === 'object') ? detail.code : undefined;
          setErrInfo(mapSocialAuthError(t, status, code));
          if (status === 503) setError(code || 'coming_soon');
          else if (status === 401) setError(code || 'verification_failed');
          else setError(code || 'network');
        }
        return;
      }

      // ── No credential at all
      setError('missing_id_token');
    })();
  }, [location.search]);

  return (
    <div
      data-testid="apple-callback-page"
      className="min-h-screen flex items-center justify-center bg-background px-4"
    >
      {!error ? (
        <div className="text-center" role="status" aria-live="polite" aria-busy="true">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" aria-hidden="true" />
          <p className="mt-3 text-sm text-muted-foreground">Signing you in…</p>
        </div>
      ) : (
        <div className="text-center max-w-md" role="alert">
          <XCircle className="h-10 w-10 text-red-600 mx-auto" aria-hidden="true" />
          <p className="mt-3 text-sm font-semibold text-red-800" data-testid="apple-callback-error">
            {errInfo ? errInfo.title : error}
          </p>
          <p className="mt-2 text-xs text-muted-foreground" data-testid="apple-callback-error-human">
            {errInfo ? errInfo.message : humanErrorMessage(error)}
          </p>
          <button
            className="mt-4 underline text-primary text-sm"
            onClick={() => navigate('/login', { replace: true })}
            data-testid="apple-callback-back-to-login"
          >
            Back to sign in
          </button>
        </div>
      )}
    </div>
  );
}

function humanErrorMessage(code) {
  switch (code) {
    case 'user_cancelled_authorize':
    case 'cancelled':
      return 'You cancelled the Apple sign-in on Apple\'s page.';
    case 'missing_id_token':
      return 'Apple did not return a credential. This is a callback-configuration issue on TreatX — please contact support.';
    case 'state_mismatch':
      return 'Security check failed (state mismatch). Please start the sign-in again from the login page.';
    case 'verification_failed':
    case 'invalid_id_token':
      return 'TreatX could not verify Apple\'s response. Please try again.';
    case 'email_not_verified_by_apple':
      return 'Apple has not verified ownership of this email address.';
    case 'apple_oauth_disabled':
    case 'coming_soon':
      return 'Apple Sign-In is not enabled on this environment yet.';
    case 'handle_expired':
    case 'handle_not_found_or_expired':
      return 'The sign-in link expired before it could be completed. Please try again.';
    case 'exchange_failed':
      return 'Could not complete sign-in with TreatX. Please try again.';
    case 'resolver_error':
      return 'Your Apple account could not be linked to a TreatX profile. Please contact support.';
    default:
      return 'Unexpected error during Apple sign-in. Please try again.';
  }
}

// Mirrors the storage side-effects of UnifiedLoginPage.applyLoginResult.
// A hard reload after this call causes AuthProvider + RBACProvider to
// re-hydrate from localStorage, so no in-memory context wiring is
// required here.
function applySocialLoginResult(data) {
  try {
    // Purge legacy mirrors first (SECURITY-S4 storage truth collapse).
    [
      'treatx_unified_token', 'treatx_unified_user', 'treatx_user',
      'treatx_rbac_permissions', 'treatx_rbac_role_assignments',
      'treatx_admin', 'treatx_patient_token',
      'treatx_coordinator_token', 'treatx_coordinator_user',
      'clinicRepresentative',
    ].forEach((k) => { try { localStorage.removeItem(k); } catch { /* noop */ } });
    try { sessionStorage.removeItem('treatx_active_clinic_id'); } catch { /* noop */ }
    try { sessionStorage.removeItem('treatx_clinic_user'); } catch { /* noop */ }

    if (data.user) localStorage.setItem('treatx_unified_user', JSON.stringify(data.user));
    localStorage.setItem('treatx_rbac_permissions', JSON.stringify(data.permissions || []));
    localStorage.setItem('treatx_rbac_role_assignments', JSON.stringify(data.role_assignments || []));
    localStorage.setItem('treatx_rbac_workspaces', JSON.stringify(data.workspaces || []));

    // Signal other tabs / providers that identity changed.
    try { window.dispatchEvent(new Event('treatx:identity-changed')); } catch { /* noop */ }
  } catch { /* fail-open; hard reload will still bring the app up */ }
}
